import { useMemo } from 'react'
import { Line } from '@react-three/drei'
import { paraArray } from '../../dominio/geometria/vetor3'
import type { ProporcoesVitima } from '../../dominio/didatico/perfisVitima'
import {
  POSE_SENTADA,
  eixoColunaDaVitima,
  type ColocacaoVitima,
  type PoseVitima,
} from '../../dominio/didatico/rigVitima'
import { CORES_SEMANTICAS } from '../../dominio/didatico/tokens'
import { ColarCervical3D } from './ColarCervical3D'
import { SegmentoCorporal } from './SegmentoCorporal'
import { geometriaDoTronco } from './corpoHumano'
import { Calcado3D, Mao3D, Rosto3D, corDoCabelo, type Casca } from './DetalhesDoCorpo'
import { Cerebro3D, OrgaosTronco3D } from '../cinematica/OrgaosInternos3D'

import type { RegiaoAnatomicaVitima } from '../../dominio/tabletop/tipos'

/** Calça, tênis e sola: contrastam com a camisa (`vitimaRoupa`) para o corpo ler em partes. */
const COR_CALCA = '#5d5a50'
const COR_CALCADO = '#d9dde3'
const COR_SOLA = '#2e3238'

interface Props {
  proporcoes: ProporcoesVitima
  colocacao: ColocacaoVitima
  pose?: PoseVitima
  /** desenha o eixo anatômico da coluna */
  mostrarEixoColuna?: boolean
  /** versão fantasma, usada para comparar rotas ou marcar posição anterior */
  fantasma?: boolean
  /** imobilização por colar cervical rígido */
  colarCervical?: boolean
  /** Modo Raio-X com visualização de órgãos internos (3ª colisão do trauma) */
  modoRaioX?: boolean
  /** membro com hemorragia visível (tingido de sangue escuro no 3D) */
  regiaoHemorragia?: RegiaoAnatomicaVitima | string | readonly (RegiaoAnatomicaVitima | string)[]
}

/**
 * Manequim procedural articulado.
 *
 * A hierarquia é pelve → tronco → (cabeça, braços) e pelve → coxas → pernas.
 * Cada articulação é um `<group>` com pivô coerente, então a mesma pose vale
 * para adulto e criança: só as proporções mudam.
 *
 * Quando o MakeHuman entrar, este componente é substituído sem tocar em
 * cálculo de ângulo, trajetória, timeline ou âncoras.
 */
export function VitimaProcedural({
  proporcoes,
  colocacao,
  pose = POSE_SENTADA,
  mostrarEixoColuna = false,
  fantasma = false,
  colarCervical = false,
  modoRaioX = false,
  regiaoHemorragia,
}: Props) {
  const p = proporcoes
  const corPele = modoRaioX ? '#38bdf8' : fantasma ? '#7d8fa0' : CORES_SEMANTICAS.vitima
  const corRoupa = modoRaioX ? '#0284c7' : fantasma ? '#4a5a6b' : CORES_SEMANTICAS.vitimaRoupa
  // a calça e o calçado destacam-se da camisa; fantasma e Raio-X mantêm um tom só
  const corCalca = modoRaioX ? '#0284c7' : fantasma ? '#4a5a6b' : COR_CALCA
  const corCalcado = modoRaioX ? '#0284c7' : fantasma ? '#6a7b8c' : COR_CALCADO
  const corSola = modoRaioX ? '#0369a1' : fantasma ? '#4a5a6b' : COR_SOLA
  const corCabelo = modoRaioX ? '#0284c7' : fantasma ? '#5b6b7a' : corDoCabelo(p.id)
  const corSangue = modoRaioX ? '#ef4444' : '#880808'
  const temSangue = (regiao: string) => {
    if (!regiaoHemorragia) return false
    if (Array.isArray(regiaoHemorragia)) {
      return regiaoHemorragia.includes(regiao as RegiaoAnatomicaVitima) || regiaoHemorragia.includes('corpoTodo')
    }
    return regiaoHemorragia === regiao || regiaoHemorragia === 'corpoTodo'
  }
  const opacidade = modoRaioX ? 0.22 : fantasma ? 0.32 : 1
  // só o Raio-X afina os membros: o fantasma sempre os manteve sólidos
  const opacidadeMembros = modoRaioX ? opacidade : undefined
  const casca: Casca = { transparent: fantasma || modoRaioX, opacity: opacidade }
  const geometriaTronco = geometriaDoTronco(p)

  const eixo = useMemo(
    () => eixoColunaDaVitima(colocacao, proporcoes, pose),
    [colocacao, proporcoes, pose],
  )

  const meiaPelve = p.larguraPelve / 2
  const meiaOmbro = p.larguraOmbros / 2

  // Quando sentado no veículo (quadril flexionado à frente), a canela deve avançar
  // pelo vão dos pedais e apoiar os pés sobre o assoalho, em vez de atravessar
  // o fundo do carro (onde ficaria pendurada a 2 cm do chão externo).
  const estaSentado = (pose.quadrilRad ?? 0) < -0.9
  const anguloJoelhoEfetivo = useMemo(() => {
    if (!estaSentado) return pose.joelhoRad
    const yQuadril = colocacao.posicaoPelve.y - p.alturaPelve * 0.1
    const yJoelho = yQuadril - p.comprimentoCoxa * Math.cos(pose.quadrilRad)
    const yPisoAlvo = 0.30 + 0.045
    const quedaDesejada = Math.max(0.04, yJoelho - yPisoAlvo)
    if (quedaDesejada >= p.comprimentoPerna) return pose.joelhoRad
    const anguloCanelaVertical = -Math.acos(Math.min(0.99, quedaDesejada / p.comprimentoPerna))
    const joelhoCalculado = anguloCanelaVertical - pose.quadrilRad
    return Math.max(0.12, Math.min(pose.joelhoRad, joelhoCalculado))
  }, [
    estaSentado,
    pose.joelhoRad,
    pose.quadrilRad,
    colocacao.posicaoPelve.y,
    p.alturaPelve,
    p.comprimentoCoxa,
    p.comprimentoPerna,
  ])
  const anguloCanelaTotal = (pose.quadrilRad ?? 0) + anguloJoelhoEfetivo

  return (
    // o three compila `transparent` no shader (OPAQUE): alternar o Raio-X sem
    // recriar os materiais manteria a casca opaca escondendo os órgãos
    <group key={modoRaioX ? 'raio-x' : 'normal'}>
      <group
        position={[colocacao.posicaoPelve.x, colocacao.posicaoPelve.y, colocacao.posicaoPelve.z]}
        rotation={[0, colocacao.guinadaRad + pose.pelveTorcaoRad, 0]}
      >
        {/* grupo aninhado: a guinada vem primeiro, a reclinação depois em torno
            do eixo lateral já girado, e a rolagem axial por último em torno do eixo da coluna. */}
        <group rotation={[-(colocacao.reclinacaoRad ?? 0), 0, 0]}>
          <group rotation={[0, colocacao.rolagemRad ?? 0, 0]}>
            {/* pelve: elipsoide, para não ter quina de caixa sob a camisa */}
            <mesh scale={[(p.larguraPelve / 2) * 1.04, (p.alturaPelve / 2) * 1.2, (p.profundidadeTronco / 2) * 1.06]}>
              <sphereGeometry args={[1, 20, 14]} />
              <meshStandardMaterial color={temSangue('pelve') ? corSangue : corCalca} roughness={0.7} {...casca} />
            </mesh>

        {/* tronco: inclinação, torção e inclinação lateral saem do rig */}
        <group
          position={[0, p.alturaPelve / 2, 0]}
          rotation={[pose.troncoInclinacaoRad, pose.troncoTorcaoRad, pose.troncoLateralRad]}
        >
          <mesh geometry={geometriaTronco}>
            <meshStandardMaterial color={temSangue('torax') || temSangue('abdome') ? corSangue : corRoupa} roughness={0.7} {...casca} />
          </mesh>

          {/* Órgãos viscerais torácicos e abdominais da 3ª Colisão */}
          {modoRaioX && (
            <OrgaosTronco3D
              alturaTronco={p.alturaTronco}
              larguraTronco={p.larguraTronco}
              profundidadeTronco={p.profundidadeTronco}
            />
          )}

          {/* cabeça e pescoço */}
          <group
            position={[0, p.alturaTronco, 0]}
            rotation={[pose.cabecaInclinacaoRad, pose.cabecaTorcaoRad, 0]}
          >
            <mesh position={[0, p.alturaPescoco / 2 - 0.01, 0]}>
              <cylinderGeometry args={[p.raioMembro * 0.72, p.raioMembro * 0.85, p.alturaPescoco + 0.02, 12]} />
              <meshStandardMaterial color={temSangue('cabeca') ? corSangue : corPele} roughness={0.65} transparent={fantasma || modoRaioX} opacity={opacidade} />
            </mesh>
            {colarCervical && (
              <ColarCervical3D
                raioPescoco={p.raioMembro * 0.8}
                alturaPescoco={p.alturaPescoco}
              />
            )}
            <mesh position={[0, p.alturaPescoco + p.raioCabeca, 0]} scale={[0.92, 1.08, 1]}>
              <sphereGeometry args={[p.raioCabeca, 18, 14]} />
              <meshStandardMaterial color={temSangue('cabeca') ? corSangue : corPele} roughness={0.6} transparent={fantasma || modoRaioX} opacity={opacidade} />
            </mesh>
            {/* rosto e cabelo; no Raio-X a casca fica lisa para não esconder o encéfalo */}
            {!modoRaioX && (
              <group position={[0, p.alturaPescoco + p.raioCabeca, 0]}>
                <Rosto3D
                  raioCabeca={p.raioCabeca}
                  corPele={temSangue('cabeca') ? corSangue : corPele}
                  corCabelo={corCabelo}
                  cabeloLongo={p.id === 'adultoFeminino'}
                  casca={casca}
                />
              </group>
            )}
            {/* Encéfalo 3D com Coup-Contrecoup no Modo Raio-X */}
            {modoRaioX && (
              <group position={[0, p.alturaPescoco + p.raioCabeca, 0]}>
                <Cerebro3D raioCabeca={p.raioCabeca} />
              </group>
            )}
          </group>

          {/* braços */}
          {[-1, 1].map((lado) => {
            const regiaoBraco = lado === -1 ? 'bracoEsquerdo' : 'bracoDireito'
            const sangrandoBraco = temSangue(regiaoBraco)
            return (
              <group
                key={lado}
                position={[lado * meiaOmbro, p.alturaTronco - 0.06, 0]}
                rotation={[pose.ombroRad, 0, lado * 0.18]}
              >
                <SegmentoCorporal forma="braco" comprimento={p.comprimentoBraco} raio={p.raioMembro * 0.85} cor={sangrandoBraco ? corSangue : corRoupa} opacidade={opacidadeMembros}>
                  <group rotation={[pose.cotoveloRad, 0, 0]}>
                    <SegmentoCorporal forma="antebraco" comprimento={p.comprimentoAntebraco} raio={p.raioMembro * 0.72} cor={sangrandoBraco ? corSangue : corPele} opacidade={opacidadeMembros}>
                      <Mao3D raioMembro={p.raioMembro} cor={sangrandoBraco ? corSangue : corPele} lado={lado as -1 | 1} casca={casca} />
                    </SegmentoCorporal>
                  </group>
                </SegmentoCorporal>
              </group>
            )
          })}
        </group>

        {/* pernas: quadril na pelve, joelho na ponta da coxa */}
        {[-1, 1].map((lado) => {
          const regiaoCoxa = lado === -1 ? 'coxaEsquerda' : 'coxaDireita'
          const regiaoPerna = lado === -1 ? 'pernaEsquerda' : 'pernaDireita'
          const sangrandoCoxa = temSangue(regiaoCoxa) || temSangue('membroInferior')
          const sangrandoPerna = temSangue(regiaoPerna) || temSangue('membroInferior')
          return (
            <group
              key={lado}
              position={[lado * meiaPelve * 0.55, -p.alturaPelve * 0.1, p.profundidadeTronco * 0.1]}
              rotation={[pose.quadrilRad, 0, lado * pose.abducaoCoxaRad]}
            >
              <SegmentoCorporal forma="coxa" comprimento={p.comprimentoCoxa} raio={p.raioMembro * 1.15} cor={sangrandoCoxa ? corSangue : corCalca} opacidade={opacidadeMembros}>
                <group rotation={[anguloJoelhoEfetivo, 0, 0]}>
                  <SegmentoCorporal forma="perna" comprimento={p.comprimentoPerna} raio={p.raioMembro * 0.92} cor={sangrandoPerna ? corSangue : corCalca} opacidade={opacidadeMembros}>
                    {/* Pé calçado: articulado no tornozelo e apoiado plano sobre o assoalho */}
                    <group rotation={[estaSentado ? -anguloCanelaTotal : 0.85, 0, 0]}>
                      <Calcado3D
                        raioMembro={p.raioMembro}
                        corCalcado={sangrandoPerna ? corSangue : corCalcado}
                        corSola={corSola}
                        casca={casca}
                      />
                    </group>
                  </SegmentoCorporal>
                </group>
              </SegmentoCorporal>
            </group>
          )
        })}
          </group>
        </group>
      </group>

      {mostrarEixoColuna && (
        <Line
          points={[paraArray(eixo.baseColuna), paraArray(eixo.topoColuna)]}
          color={CORES_SEMANTICAS.eixoAnatomico}
          lineWidth={5}
        />
      )}
    </group>
  )
}
